import { api } from "../api/client";

/**
 * The show API, as the panel sees it (M0.7).
 *
 * Types hand-written against `packages/functions/src/lib/shows.ts` rather than generated. There
 * is no schema between the two — the API is hand-rolled JSON — so this file is the contract, and
 * the thing that keeps it honest is that both sides are in one repository and change together.
 */

export type ShowState = "draft" | "scheduled" | "live" | "ended" | "cancelled";

export interface Show {
  id: string;
  publicId: string;
  title: string;
  state: ShowState;
  hostAccountId: string;
  scheduledStartAt: string | null;
  /**
   * `R-HOST-38` — what the room calls the host for this show. Null means the account's display
   * name is used, which is what the field shows as its placeholder.
   */
  hostName: string | null;
  publishedAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  cancelledAt: string | null;
  /**
   * Quarter turns clockwise the customer's player applies to the video (`R-VID-8`).
   *
   * Zero for a camera the right way up. A host with one mounted on its side records the angle
   * here and the room turns the picture; the stream is published untouched.
   */
  videoRotation: number;
  /**
   * The share link's preview image (`R-HOST-37`), as a path on the customer domain — see
   * `mediaUrl`. Null by default, which means the link previews without an image.
   */
  thumbnailPath: string | null;
  /**
   * `R-END-6a` — the show this one's link redirects to once it is over, or null for none. Only an
   * ended or cancelled show can have one.
   */
  redirectShowId: string | null;
  /** `R-SHOW-26a` — a rehearsal, which the `/live` link never sends anybody to. */
  testRun: boolean;
  createdAt: string;
}

/**
 * What a lot can be told to do. `skip` is deliberately absent — see `LOT_STATES` in
 * `packages/core/src/shows/lots.ts`; the panel labels `withdraw` for the situation instead.
 */
export type LotAction =
  "feature" | "open" | "close" | "withdraw" | "relist" | "refresh";

export type LotState =
  "queued" | "featured" | "selling" | "sold" | "unsold" | "withdrawn" | "settled";

export interface LotSnapshot {
  productTitle: string;
  variantTitle: string | null;
  sku: string | null;
  descriptionHtml: string | null;
  images: { url: string; alt: string | null }[];
  shopifyPrice: string;
  compareAtPrice: string | null;
  /**
   * What the shop paid for one (`R-LOT-31`). **Panel only** — the customer application has no
   * field for it and the route that serves customers does not send it.
   *
   * Null where Shopify holds no cost, which is common and is not a fault.
   */
  unitCost: string | null;
  /**
   * `unitCost` plus 20% VAT where Shopify charges tax on the variant — the lowest price that does
   * not lose money, since every price here is VAT-inclusive; the cost itself where there is no VAT,
   * or where a snapshot predates the VAT flag (a refresh fills it in). Panel only, like the cost.
   * Null only where there is no cost.
   */
  breakEven: string | null;
  /**
   * `R-LOT-30` — the pack's best-before date, `YYYY-MM-DD`, from the variant's metafield. A date,
   * not an instant: shown with `formatBestBefore` and never made into a `Date`. Null where none is
   * set, which is most products that are not food.
   */
  bestBefore: string | null;
  capturedAt: string;
}

/**
 * How an auction lot is configured (spec §7.2, §10.5, M1.1).
 *
 * The **starting price is not here** — it is `Lot.price`, which is the same field a fixed-price
 * lot charges from. One number, one input, one thing to get wrong.
 */
export interface AuctionSettings {
  minIncrement: string;
  durationSeconds: number;
  counterBidSeconds: number;
  closeMode: "standard" | "sudden";
}

export interface Lot {
  id: string;
  position: number;
  variantId: string;
  productId: string;
  saleMode: "fixed" | "auction";
  /** The starting price, on an auction lot. */
  price: string;
  /**
   * Present whenever the row carries settings, which a lot switched back to fixed price still
   * does — the panel sees what was typed, so switching modes is reversible. Null on a lot that
   * has never been configured as an auction.
   */
  auction: AuctionSettings | null;
  /**
   * What the lot offers. **Zero on a queued lot means *decide when featured*** (M1.13): the
   * feature offers the global default share of whatever Shopify has then.
   */
  allocated: number;
  consumed: number;
  /** Units Shopify is holding for this lot — its sales, until their orders exist (M1.13). */
  reserved: number;
  /** Units whose Shopify order has been created, and so are no longer held. */
  ordered: number;
  /** A shortlisted lot the host did not price: it takes Shopify's price when featured. */
  priceFollowsShopify: boolean;
  /** `R-LOT-33` — whether the room shows *X left* beside this lot's price. Fixed price only. */
  showRemaining: boolean;
  /** `R-LOT-34` — a fixed-price sale's time limit in seconds, or null for none. */
  sellSeconds: number | null;
  /** When a timed lot's sale ends, once it has been opened. Null otherwise. */
  closesAt: string | null;
  /**
   * For a shortlisted lot, the catalogue's stock figure for its variant and when it was last heard
   * (M1.13). Null for every other lot, and for a variant the copy does not hold.
   */
  stock: {
    available: number | null;
    stockSyncedAt: string | null;
    price: string;
  } | null;
  remaining: number;
  /**
   * Which round an auction lot is on (`R-AUC-22`). `1` on a fixed-price lot, where it means
   * nothing — the quantity of an auction lot is the number of rounds it will run, so `round`
   * against `allocated` is how far through the lot the show is (`R-AUC-28`).
   */
  round: number;
  /** What may actually be sold — what is left of the offer. */
  sellable: number;
  state: LotState;
  featuredAt: string | null;
  openedAt: string | null;
  closedAt: string | null;
  snapshot: LotSnapshot | null;
}

/**
 * `R-INV-8` as amended by M1.13, as the panel shows it: what Shopify is holding for the show's
 * sales against what it should be.
 */
export interface Reconciliation {
  reserved: number;
  released: number;
  sold: number;
  /** Units held at Shopify for the show now. */
  held: number;
  /** Units that should be: sold, and not yet in a created order. */
  owed: number;
  pending: number;
  failed: number;
  balanced: boolean;
}

/** One recent sale, for presenter mode (`R-HOST-39`). */
export interface RecentSale {
  purchaseId: string;
  name: string | null;
  title: string | null;
  /** Goods only, without shipping. */
  amount: string;
  origin: "claim" | "win";
  paidAt: string;
}

export interface ShowDetail {
  show: Show;
  lots: Lot[];
  /** The last four sales, newest first. */
  recentSales: RecentSale[];
  summary: {
    lots: number;
    /** Lots that sold **out** — not units. `unitsSold` is the other question (§17.4). */
    sold: number;
    unitsSold: number;
    remaining: number;
    /**
     * §17.4's *revenue so far*. Two figures because `R-ANL-5` says so — goods and shipping are
     * different things and are never added into one headline.
     */
    /**
     * `R-ANL-5` — goods and delivery apart. `auction` and `fixed` split `goods` by how each sale
     * was made, and always add up to it.
     */
    takings: {
      goods: string;
      auction: string;
      fixed: string;
      shipping: string;
      purchases: number;
    };
    /** `R-ANL-1` — live viewers, the same figure the room shows its audience. */
    viewers: number;
    inventory: Reconciliation;
  };
}

/** One reason a show cannot be published (`R-HOST-9`). */
export interface LotProblem {
  lotId: string;
  position: number;
  code: string;
  message: string;
}

/** The arithmetic a feature was decided on (`shows/featuring.ts`). */
export interface StockCheck {
  available: number;
  unheld: number;
  effective: number;
  minimum: number;
}

/** What the feature sheet may send. Every field is optional; the server fills in the rest. */
export interface FeatureChoices {
  saleMode?: "fixed" | "auction";
  price?: string;
  quantity?: number;
  auction?: AuctionSettings;
  showRemaining?: boolean;
  sellSeconds?: number | null;
}

export const shows = {
  list: () => api.get<{ shows: Show[] }>("/api/shows"),

  create: (title: string, scheduledStartAt: string | null) =>
    api.post<{ show: Show }>("/api/shows", { title, scheduledStartAt }),

  read: (id: string) => api.get<ShowDetail>(`/api/shows/${id}`),

  edit: (
    id: string,
    edits: {
      title?: string;
      scheduledStartAt?: string | null;
      /** `null` clears it and returns the show to the host account's display name. */
      hostName?: string | null;
      /** `R-VID-8` — 0 to 3. The route publishes `show.updated` so open rooms re-read. */
      videoRotation?: number;
      /** `R-END-6a` — sent alone, on an ended or cancelled show. `null` removes the redirect. */
      redirectShowId?: string | null;
      /** `R-SHOW-26a` — sent alone, in any state. */
      testRun?: boolean;
    },
  ) => api.patch<{ show: Show }>(`/api/shows/${id}`, edits),

  /** `R-HOST-37` — `image` is a JPEG `data:` URL, already cropped to 1200 × 630 (`thumbnail.ts`). */
  setThumbnail: (id: string, image: string) =>
    api.put<{ show: Show }>(`/api/shows/${id}/thumbnail`, { image }),

  /** Back to no thumbnail, which is where every show starts. */
  removeThumbnail: (id: string) =>
    api.del<{ show: Show }>(`/api/shows/${id}/thumbnail`),

  /**
   * The four transitions of spec §8. Each answers 409 if somebody else took it first — except
   * `publish`, which a `scheduled` show answers 200 and `topUp: true` to. None of them moves stock
   * since M1.13.
   */
  act: (id: string, action: "publish" | "live" | "end" | "cancel") =>
    api.post<{
      show: Show;
      /** True where the show was already scheduled, so nothing changed. */
      topUp?: boolean;
      /** What is held for the show's sales, which settlement gives back order by order. */
      inventory?: Reconciliation;
      /** Lots sent to give back stock held beyond their sales — a pre-M1.13 allocation. */
      rebalanced?: number;
      /** Spec §15.2 — orders closed and discarded. Their settlement runs in the background. */
      orders?: { closed: number; discarded: number };
      /** False only when the background queue refused and the request did the work itself. */
      background?: boolean;
      /** `R-GIVE-5` — how many orders each giveaway went to, out of how many could have won. */
      giveaways?: {
        kind: "buyers";
        quantity: number;
        eligible: number;
        winners: number;
      }[];
    }>(`/api/shows/${id}/${action}`),

  /**
   * Adds a product to the shortlist (M1.13). Holds no stock; everything is optional but the
   * variant — no quantity means *decide when featured*, no price means *Shopify's, then*.
   */
  addLot: (
    showId: string,
    lot: {
      variantId: string;
      allocated?: number;
      price?: string;
      saleMode?: "fixed" | "auction";
      auction?: AuctionSettings;
      showRemaining?: boolean;
      sellSeconds?: number | null;
    },
  ) => api.post<{ lot: Lot }>(`/api/shows/${showId}/lots`, lot),

  /**
   * Features a product in a live show (M1.13): a shortlisted lot or a variant straight from
   * search. Checks Shopify's stock and decides the quantity; the refusals are named in `reason`.
   */
  feature: (
    showId: string,
    target: { lotId: string } | { variantId: string },
    choices: FeatureChoices = {},
  ) =>
    api.post<{ lot: Lot; unfeatured: string[]; created: boolean; stock: StockCheck }>(
      `/api/shows/${showId}/feature`,
      { ...target, ...choices },
    ),

  editLot: (
    showId: string,
    lotId: string,
    edits: {
      price?: string;
      allocated?: number;
      position?: number;
      saleMode?: "fixed" | "auction";
      /**
       * Sent whole, always. The four settings are all-or-nothing in Postgres, and the route
       * fills any field left out from what the lot already holds — so a partial object is a
       * merge rather than a reset, and this form has all four on screen anyway.
       */
      auction?: Partial<AuctionSettings>;
      /** `R-LOT-33`. Accepted until the lot finishes selling, unlike the price. */
      showRemaining?: boolean;
      /** `R-LOT-34`. Only while queued, like the price. Null removes the limit. */
      sellSeconds?: number | null;
    },
  ) => api.patch<{ lot: Lot }>(`/api/shows/${showId}/lots/${lotId}`, edits),

  removeLot: (showId: string, lotId: string) =>
    api.del<{ removed: string }>(`/api/shows/${showId}/lots/${lotId}`),

  actOnLot: (showId: string, lotId: string, action: LotAction) =>
    api.post<{ lot: Lot }>(`/api/shows/${showId}/lots/${lotId}/${action}`),

  /** Settlement, as an operator sees it (M0.12, `R-SYNC-16`). */
  orders: (showId: string) => api.get<ShowOrders>(`/api/shows/${showId}/orders`),

  /** Puts every unsettled order back on the settlement queue. Safe to press again. */
  settle: (showId: string) =>
    api.post<ShowOrders & { requested: number }>(`/api/shows/${showId}/orders/settle`),

  /** The show's giveaway (spec §15.10), and who won it once the show has ended. */
  giveaway: (showId: string) =>
    api.get<GiveawayAnswer>(`/api/shows/${showId}/giveaway`),

  /** Sets it, replacing whatever was there. Refused once the show has ended (`R-GIVE-8`). */
  setGiveaway: (showId: string, variantId: string, quantity: number) =>
    api.put<GiveawayAnswer>(`/api/shows/${showId}/giveaway`, { variantId, quantity }),

  removeGiveaway: (showId: string) =>
    api.del<GiveawayAnswer>(`/api/shows/${showId}/giveaway`),
};

// -------------------------------------------------------------------------------------------
// Giveaways (spec §15.10) — hand-written against `packages/functions/src/giveaway.ts`.
// -------------------------------------------------------------------------------------------

export interface Giveaway {
  id: string;
  /** `buyers` — drawn among the show's buyers (`R-GIVE-2`). The only kind so far. */
  kind: "buyers";
  variantId: string;
  productId: string;
  productTitle: string;
  variantTitle: string | null;
  sku: string | null;
  imageUrl: string | null;
  quantity: number;
  /** `pending` until the show ends; `drawn` after (`R-GIVE-4`). */
  state: "pending" | "drawn";
  drawnAt: string | null;
  /** The catalogue's stock figure — for warning, never for deciding (`R-GIVE-7`). */
  available: number | null;
  stockSyncedAt: string | null;
}

export interface GiveawayAnswer {
  giveaway: Giveaway | null;
  /** Empty until the show has ended. Staff-only route, so a name and an email. */
  winners: { showOrderId: string; customer: string; email: string }[];
  /** Whether it can still be set, changed or removed (`R-GIVE-8`). */
  editable: boolean;
  quantityMax: number;
}

/**
 * One customer's order in one show, on its way to Shopify (spec §15.5).
 *
 * `open` while the show runs, `closed` the moment it ends, `completed` once the draft has become
 * a real Shopify order (`R-SYNC-12`), `cancelled` where there was nothing left to complete
 * (`R-SYNC-15`). **`closed` is the one that matters**: an order still in it after the show has
 * ended is `R-SYNC-16`'s incident.
 */
export interface ShowOrderRow {
  id: string;
  state: "open" | "closed" | "completed" | "cancelled";
  /** The display name, or the email where there is none. Staff-only route. */
  customer: string | null;
  /** The customer's email, shown under the name. */
  email: string | null;
  items: number;
  /** Decimal string — what actually left the customer's account (`R-SYNC-13`). */
  charged: string;
  shopifyDraftOrderId: string | null;
  shopifyOrderId: string | null;
  /** The order in the Shopify admin, or null before there is an order to open. */
  shopifyOrderUrl: string | null;
  /** Verbatim from `show_order.settlement_problem`; see `shows/settlement.ts` for the three. */
  problem: string | null;
  syncedAt: string | null;
  completedAt: string | null;
}

export interface ShowOrders {
  orders: ShowOrderRow[];
  /** How many are neither completed nor cancelled. The number `R-SYNC-16` is about. */
  outstanding: number;
  /** Of those, how many the worker refused and recorded a reason for. Somebody must look. */
  failed: number;
  /** And how many are merely not there yet — the queue, seconds after a show ends. */
  settling: number;
}

/**
 * The share link a host copies (`R-HOST-33`).
 *
 * Built from the customer application's origin, which differs per stage: hard-coding the
 * production one would hand out a link to the wrong stage from every branch deploy, and deriving
 * it from the panel's own address is wrong under `sst dev`, where the two are ports on
 * `localhost`. `infra/web.ts` passes it in as `VITE_CUSTOMER_ORIGIN`.
 *
 * Read through a cast rather than off `import.meta.env` directly, because `sst-env.d.ts` is
 * generated by SST from the deployed stage's environment and declares only what that stage
 * happens to set. Typing against a generated file means `npm run typecheck` passes or fails
 * depending on whether somebody has run `sst dev` lately, which is not a property a build should
 * have.
 */
export function shareLink(publicId: string): string {
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  return `${env.VITE_CUSTOMER_ORIGIN ?? window.location.origin}/s/${publicId}`;
}

/**
 * Where an image the platform stores (`/media/*`, `infra/media.ts`) can be loaded from.
 *
 * Not `VITE_CUSTOMER_ORIGIN`: under `sst dev` that is the customer application's Vite server,
 * which has no `/media` — the bucket is only ever served by the stage's real customer
 * distribution, which `VITE_MEDIA_ORIGIN` names on every stage. Read through a cast for the reason
 * `shareLink` gives.
 */
export function mediaUrl(path: string): string {
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  return `${env.VITE_MEDIA_ORIGIN ?? env.VITE_CUSTOMER_ORIGIN ?? window.location.origin}${path}`;
}

// -------------------------------------------------------------------------------------------
// Video (M0.9)
// -------------------------------------------------------------------------------------------

/** Whether a show is ready to go live, and why not, from `packages/core/src/video/preflight.ts`. */
export interface Preflight {
  ready: boolean;
  lots: number;
  problems: LotProblem[];
  publishing: boolean;
  /** False when IVS could not be asked, in which case the stream half was waived, not passed. */
  streamKnown: boolean;
  /**
   * Another device holds the broadcast and something is publishing (`R-VID-39`). Never this panel's
   * own broadcast, provided the poll sent this panel's device.
   */
  elsewhere: boolean;
}

export interface BroadcastToken {
  token: string;
  participantId: string;
  expiresInSeconds: number;
}

export const video = {
  /**
   * A publish token, and the show's stage created if this is the first ask (spec §17.3).
   *
   * Called again on every reconnection attempt rather than cached, which is what makes `R-VID-6`
   * — a host who changes network rejoins the *same* stream — a property of the token rather than
   * of the browser holding one.
   *
   * `device` is this panel's own id (`R-VID-39`): the show is broadcast from one device at a time,
   * and a token asked for by any other is refused with `409 broadcasting-elsewhere`.
   */
  broadcast: (showId: string, device: string) =>
    api.post<{ broadcast: BroadcastToken; preflight: Preflight }>(
      `/api/shows/${showId}/broadcast`,
      { device },
    ),

  /** Gives the broadcast up, so another device may take it. A no-op from any but the holder. */
  release: (showId: string, device: string) =>
    api.del<{ released: boolean }>(
      `/api/shows/${showId}/broadcast?device=${encodeURIComponent(device)}`,
    ),

  /** What stands between this show and going live (`R-HOST-11`). Polled while the host sets up. */
  preflight: (showId: string, device: string) =>
    api.get<{ state: ShowState; videoRotation: number; preflight: Preflight }>(
      `/api/shows/${showId}/preflight?device=${encodeURIComponent(device)}`,
    ),
};
